import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, History, Loader2, Pause, Pencil, Play, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import FrDateField from "@/components/FrDateField";

const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const TARGETS = [["all", "Tous"], ["clients", "Pax"], ["drivers", "Covoit."], ["user", "1 user"], ["city", "Ville"]];
const SOUNDS = [["discreet", "Discret"], ["system", "Système"], ["price", "Price"]];
const STATUS = { active: ["Active", "bg-[#D6F5D6]"], paused: ["En pause", "bg-[#FFF3CC]"], done: ["Terminée", "bg-secondary/60"] };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Europe/Paris", weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtLocal = (s) => { const [d, t] = String(s).split("T"); const [y, m, j] = d.split("-"); return `${j}/${m}/${y} ${String(t || "").slice(0, 5)}`; };
const todayIso = () => new Date().toISOString().slice(0, 10);
const EMPTY = { title: "", body: "", sound: "system", target: "all", target_user_id: null, city: "", city_role: "all",
  schedule: { type: "once", at: "", time: "09:00", every_days: 2, start_date: todayIso(), weekdays: [0] } };
const inputCls = "w-full rounded-xl bg-secondary/40 px-3 py-2.5 text-[14px] font-medium street-border-thin outline-none focus:bg-white";

export function scheduleLabel(s) {
  if (s.type === "once") return `Une fois · ${fmtLocal(s.at)}`;
  if (s.type === "interval") return s.every_days === 1 ? `Tous les jours à ${s.time}` : `Tous les ${s.every_days} jours à ${s.time} (dès le ${fmtLocal(s.start_date).slice(0, 10)})`;
  return `Chaque ${s.weekdays.map((d) => DAYS[d]).join(", ")} à ${s.time}`;
}
const targetLabel = (n) => (n.target === "city" ? `Ville : ${n.city}${n.city_role !== "all" ? ` (${n.city_role === "clients" ? "passagers" : "conducteurs"})` : ""}` : n.target === "user" ? `Utilisateur : ${n.target_user_name || "—"}` : TARGETS.find((t) => t[0] === n.target)?.[1]);

function Chips({ options, value, onChange, testid, multi }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(([v, l]) => {
        const on = multi ? value.includes(v) : value === v;
        return (
          <button key={v} type="button" data-testid={`${testid}-${v}`}
            onClick={() => onChange(multi ? (on ? value.filter((x) => x !== v) : [...value, v].sort()) : v)}
            className={`street-press rounded-xl px-3 py-1.5 text-[11px] font-black uppercase tracking-widest street-border-thin ${on ? "bg-primary text-primary-foreground" : "bg-white"}`}>{l}</button>
        );
      })}
    </div>
  );
}

function ScheduleFields({ s, set }) {
  const up = (k) => (v) => set({ ...s, [k]: v });
  return (
    <div className="space-y-2">
      <Chips options={[["once", "Une fois"], ["interval", "Tous les X jours"], ["weekly", "Jours de la semaine"]]} value={s.type} onChange={up("type")} testid="sched-type" />
      {s.type === "once" && (
        <input type="datetime-local" value={s.at} onChange={(e) => up("at")(e.target.value)} className={inputCls} data-testid="sched-at" />
      )}
      {s.type === "interval" && (
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[10px] font-black uppercase text-foreground/60">Tous les (jours)
            <input type="number" min={1} max={365} value={s.every_days} onChange={(e) => up("every_days")(Number(e.target.value))} className={inputCls} data-testid="sched-every-days" />
          </label>
          <label className="text-[10px] font-black uppercase text-foreground/60">Heure
            <input type="time" value={s.time} onChange={(e) => up("time")(e.target.value)} className={inputCls} data-testid="sched-time" />
          </label>
          <label className="col-span-2 text-[10px] font-black uppercase text-foreground/60">À partir du
            <FrDateField value={s.start_date} onChange={up("start_date")} testid="sched-start-date" className="h-11" />
          </label>
        </div>
      )}
      {s.type === "weekly" && (
        <>
          <Chips options={DAYS.map((d, i) => [i, d])} value={s.weekdays} onChange={up("weekdays")} testid="sched-day" multi />
          <input type="time" value={s.time} onChange={(e) => up("time")(e.target.value)} className={inputCls} data-testid="sched-time" />
        </>
      )}
      <p className="text-[10px] font-semibold text-foreground/50">Heure de Paris · envoi au quart d&apos;heure près.</p>
    </div>
  );
}

function SchedForm({ initial, renderUserPicker, onSaved, onCancel }) {
  const [f, setF] = useState(initial || EMPTY);
  const [targetUser, setTargetUser] = useState(initial?.target_user_id ? { id: initial.target_user_id, label: initial.target_user_name || "Utilisateur sélectionné", email: "" } : null);
  const [busy, setBusy] = useState(false);
  const up = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    if (!f.title.trim() || !f.body.trim()) return toast.error("Titre et message obligatoires");
    const payload = { ...f, target_user_id: f.target === "user" ? targetUser?.id : null };
    setBusy(true);
    try {
      if (initial?.id) await api.put(`/admin/price/scheduled-notifs/${initial.id}`, payload);
      else await api.post("/admin/price/scheduled-notifs", payload);
      toast.success(initial?.id ? "Programmation modifiée" : "Notification programmée ✅");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-2xl bg-white p-4 street-border street-shadow-sm" data-testid="sched-form">
      <input value={f.title} onChange={up("title")} placeholder="Titre" className={inputCls} data-testid="sched-title" />
      <textarea value={f.body} onChange={up("body")} rows={3} placeholder="Message" className={`${inputCls} resize-none`} data-testid="sched-body" />
      <div>
        <div className="mb-1.5 text-[11px] font-black uppercase tracking-widest text-foreground/70">Cible</div>
        <Chips options={TARGETS} value={f.target} onChange={up("target")} testid="sched-target" />
        {f.target === "user" && <div className="mt-2">{renderUserPicker(targetUser, setTargetUser)}</div>}
        {f.target === "city" && (
          <div className="mt-2 space-y-2">
            <input value={f.city} onChange={up("city")} placeholder="Ex. Nice" className={inputCls} data-testid="sched-city" />
            <Chips options={[["all", "Tous"], ["clients", "Passagers"], ["drivers", "Conducteurs"]]} value={f.city_role} onChange={up("city_role")} testid="sched-city-role" />
          </div>
        )}
      </div>
      <div>
        <div className="mb-1.5 text-[11px] font-black uppercase tracking-widest text-foreground/70">Son</div>
        <Chips options={SOUNDS} value={f.sound} onChange={up("sound")} testid="sched-sound" />
      </div>
      <div>
        <div className="mb-1.5 text-[11px] font-black uppercase tracking-widest text-foreground/70">Quand</div>
        <ScheduleFields s={f.schedule} set={(s) => setF((x) => ({ ...x, schedule: s }))} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={onCancel} className="rounded-xl bg-white py-2.5 text-[13px] font-black street-border-thin" data-testid="sched-cancel">Annuler</button>
        <button type="button" disabled={busy} onClick={save} className="street-press rounded-xl bg-primary py-2.5 text-[13px] font-black text-primary-foreground street-border-thin disabled:opacity-60" data-testid="sched-save">
          {busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : initial?.id ? "Enregistrer" : "Programmer"}
        </button>
      </div>
    </div>
  );
}

function Runs({ id }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/admin/price/scheduled-notifs/${id}/runs`).then(({ data }) => setItems(data.items)).catch(() => setItems([])); }, [id]);
  if (!items) return <Loader2 className="mt-2 h-4 w-4 animate-spin" />;
  if (!items.length) return <p className="mt-2 text-[11px] font-semibold text-foreground/60" data-testid={`sched-runs-empty-${id}`}>Aucun envoi pour l&apos;instant.</p>;
  return (
    <div className="mt-2 space-y-1" data-testid={`sched-runs-${id}`}>
      {items.map((r) => (
        <div key={r.id} className="flex justify-between rounded-lg bg-secondary/40 px-2 py-1 text-[11px] font-bold">
          <span>{fmt(r.at)}</span><span>{r.status === "sent" ? `${r.sent_to} destinataire(s)` : "Échec"}</span>
        </div>
      ))}
    </div>
  );
}

function SchedCard({ n, onEdit, reload }) {
  const [showRuns, setShowRuns] = useState(false);
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const [label, cls] = STATUS[n.status];
  return (
    <div className="rounded-2xl bg-white p-3 street-border-thin" data-testid={`sched-notif-${n.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[13px] font-black">{n.title}</div>
          <div className="line-clamp-2 text-[11px] font-semibold text-foreground/60">{n.body}</div>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black street-border-thin ${cls}`} data-testid={`sched-status-${n.id}`}>{label}</span>
      </div>
      <div className="mt-2 space-y-0.5 text-[11px] font-bold">
        <div data-testid={`sched-rule-${n.id}`}>{scheduleLabel(n.schedule)} · {targetLabel(n)}</div>
        <div className="text-foreground/60">Prochain envoi : <span data-testid={`sched-next-${n.id}`}>{n.status === "active" ? fmt(n.next_run_at) : "—"}</span> · {n.runs_count || 0} envoi(s)</div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {n.status === "active" && <IconBtn icon={Pause} label="Pause" testid={`sched-pause-${n.id}`} onClick={() => act(() => api.post(`/admin/price/scheduled-notifs/${n.id}/pause`), "Mise en pause")} />}
        {n.status !== "active" && <IconBtn icon={Play} label="Reprendre" testid={`sched-resume-${n.id}`} onClick={() => act(() => api.post(`/admin/price/scheduled-notifs/${n.id}/resume`), "Reprise")} />}
        <IconBtn icon={Pencil} label="Modifier" testid={`sched-edit-${n.id}`} onClick={() => onEdit(n)} />
        <IconBtn icon={History} label="Historique" testid={`sched-history-${n.id}`} onClick={() => setShowRuns((v) => !v)} />
        <IconBtn icon={Trash2} label="Supprimer" testid={`sched-delete-${n.id}`} danger
          onClick={() => window.confirm("Supprimer cette notification programmée ?") && act(() => api.delete(`/admin/price/scheduled-notifs/${n.id}`), "Supprimée")} />
      </div>
      {showRuns && <Runs id={n.id} />}
    </div>
  );
}

function IconBtn({ icon: Icon, label, onClick, testid, danger }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`street-press inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-widest street-border-thin ${danger ? "text-red-600" : ""}`}>
      <Icon className="h-3 w-3" />{label}
    </button>
  );
}

// Section « Notifications programmées » de l'onglet Notifs admin.
export default function ScheduledNotifsPanel({ renderUserPicker }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | notif
  const load = useCallback(() => api.get("/admin/price/scheduled-notifs").then(({ data }) => setItems(data.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-2 pt-2" data-testid="scheduled-notifs-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 font-[Outfit] text-[16px] font-black"><CalendarClock className="h-4 w-4 text-primary" />Notifications programmées</div>
        {!editing && (
          <button type="button" onClick={() => setEditing("new")} data-testid="sched-new"
            className="street-press inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[11px] font-black text-primary-foreground street-border-thin">
            <Plus className="h-3.5 w-3.5" />Programmer
          </button>
        )}
      </div>
      {editing && (
        <SchedForm key={editing === "new" ? "new" : editing.id} initial={editing === "new" ? null : editing} renderUserPicker={renderUserPicker}
          onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
      )}
      {items.length === 0 && !editing && <p className="rounded-xl bg-white p-3 text-[12px] font-semibold street-border-thin" data-testid="sched-empty">Aucune notification programmée.</p>}
      {items.map((n) => <SchedCard key={n.id} n={n} onEdit={setEditing} reload={load} />)}
    </div>
  );
}
